import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  // Surfaced at boot so missing env is loud, not silent.
  // eslint-disable-next-line no-console
  console.warn(
    '[penrose-web] VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY is not set. ' +
      'Copy .env.example to .env.local and fill them in.'
  );
}

export const supabase: SupabaseClient = createClient(url ?? '', anonKey ?? '');

/**
 * Authenticate the Realtime socket with the user's JWT.
 *
 * Supabase Realtime `postgres_changes` on RLS-protected tables (our
 * `documents` / `work_items` are member-scoped) only delivers events if the
 * Realtime connection carries the subscribing user's access token — the server
 * runs each change payload through the table's RLS SELECT policy as that user
 * before fanning it out. Without this, the socket connects as the anon role,
 * RLS authorizes nothing, and NO change events are ever sent — the symptom that
 * broke the live reconcile (the open editor never saw external/agent edits).
 *
 * `createClient` does NOT wire the auth token into Realtime automatically when
 * you pass only the anon key, so we do it explicitly here and keep it current
 * on every auth-state change. This must be the single source of truth so the
 * token is set BEFORE any channel subscribes and stays fresh across refreshes.
 */
function applyRealtimeAuth(token: string | null): void {
  // setAuth(null) drops back to the anon key for the connection — correct on
  // sign-out so a stale token isn't carried into the next (anon) session.
  supabase.realtime.setAuth(token);
}

// Prime the Realtime token from any persisted session as early as possible,
// before the app mounts an editor that opens a channel. `getSession()` reads
// the locally-stored session synchronously off disk, so this resolves fast.
void supabase.auth
  .getSession()
  .then(({ data }) => applyRealtimeAuth(data.session?.access_token ?? null))
  .catch(() => {
    /* getSession should not reject; ignore so boot is never blocked. */
  });

// Keep the Realtime token current for the life of the client. SIGNED_IN and
// INITIAL_SESSION cover the first authentication; TOKEN_REFRESHED keeps a
// long-lived editor's socket authorized after the access token rotates;
// SIGNED_OUT clears it. This is a process-global listener (one per client) so
// it is independent of which page/hook happens to open a channel.
supabase.auth.onAuthStateChange((_event, session) => {
  applyRealtimeAuth(session?.access_token ?? null);
});

/**
 * The Supabase project URL + anon key, inlined into this bundle at build time.
 * Exported so the Agent panel can hand them to the Theia backend in its
 * `session` message — the backend process does not carry the Supabase config
 * in its own environment. `null` when unset (mirrors the warning above).
 */
export const SUPABASE_URL: string | null = url ?? null;
export const SUPABASE_ANON_KEY: string | null = anonKey ?? null;
